import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import {
  Search,
  BookOpen,
  Clock,
  Circle,
  CheckCircle2,
  CircleDot,
  Zap,
  Award,
  PlayCircle,
  X,
  ChevronRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { CHAPTERS, getChapterById } from "@/tutorial/chaptersData";
import { readProgress } from "@/tutorial/progress";
import { SEARCH_INDEX, FUSE_OPTIONS } from "@/tutorial/searchIndex";
import {
  TutorialPDFExportButton,
  useTutorialPDF,
} from "@/tutorial/components/TutorialPDFExporter";

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────

/** Produce an 80-char excerpt from `content`, centred on the first matched
 *  range, with the matched substring wrapped in <strong>. */
function excerptWithHighlight(content, matches, key = "content", span = 80) {
  const match = (matches || []).find((m) => m.key === key);
  if (!match || !match.indices?.length) {
    return content.length > span ? content.slice(0, span) + "…" : content;
  }
  const [start, end] = match.indices[0];
  const half = Math.floor(span / 2);
  const from = Math.max(0, start - half);
  const to = Math.min(content.length, from + span);
  const prefix = from > 0 ? "…" : "";
  const suffix = to < content.length ? "…" : "";
  const before = content.slice(from, start);
  const hit = content.slice(start, end + 1);
  const after = content.slice(end + 1, to);
  return (
    <>
      {prefix}
      {before}
      <strong className="text-brand-blue dark:text-brand-gold">{hit}</strong>
      {after}
      {suffix}
    </>
  );
}

function getStatus(progress, chapterId) {
  if (progress[String(chapterId)] === true) return "done";
  const firstIncomplete = CHAPTERS.find((c) => progress[String(c.id)] !== true);
  if (firstIncomplete && firstIncomplete.id === chapterId) return "current";
  return "todo";
}

// ────────────────────────────────────────────────────────────────────────
// Section A — Hero
// ────────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section
      className="rounded-xl border border-brand-gold/20 bg-gradient-to-br from-[#1A3C5E] via-[#234f78] to-[#2C5F8A] px-6 py-10 text-white shadow-lg sm:px-10 sm:py-14"
      data-testid="tutorial-hero"
    >
      <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
        <BookOpen className="h-3.5 w-3.5" />
        Centre d'aide
      </div>
      <h1 className="mt-4 font-judicial text-3xl font-black leading-tight tracking-tight sm:text-5xl">
        Centre d'Aide &amp; Tutoriel VITAE-PUBLICA™
      </h1>
      <blockquote className="mt-5 max-w-3xl border-l-2 border-brand-gold pl-4 font-judicial text-lg italic leading-relaxed text-brand-gold sm:text-xl">
        « Maîtrisez l'outil qui lit un budget et y voit des vies humaines. »
      </blockquote>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Section B — Search
// ────────────────────────────────────────────────────────────────────────

function SearchBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const fuse = useMemo(() => new Fuse(SEARCH_INDEX, FUSE_OPTIONS), []);

  const results = useMemo(() => {
    if (q.trim().length < 2) return [];
    return fuse.search(q.trim()).slice(0, 8);
  }, [q, fuse]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const selectResult = useCallback(
    (item) => {
      setOpen(false);
      setQ("");
      navigate(`/tutorial/chapter/${item.chapter}#${item.anchor}`);
    },
    [navigate]
  );

  return (
    <section
      ref={wrapperRef}
      className="relative -mt-6 sm:-mt-10"
      data-testid="tutorial-search-section"
    >
      <div className="mx-auto max-w-3xl rounded-xl border border-border bg-card p-3 shadow-lg">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => q.trim().length >= 2 && setOpen(true)}
            placeholder="Rechercher dans le tutoriel... (ex: score fiabilité)"
            className="h-12 rounded-lg border-0 bg-background pl-11 pr-10 text-base shadow-sm focus-visible:ring-brand-blue"
            data-testid="tutorial-search-input"
            aria-label="Rechercher dans le tutoriel"
            autoComplete="off"
          />
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setOpen(false);
              }}
              aria-label="Effacer la recherche"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-accent"
              data-testid="tutorial-search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {open && q.trim().length >= 2 && (
          <div
            role="listbox"
            className="mt-2 max-h-[420px] overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-xl"
            data-testid="tutorial-search-results"
          >
            {results.length === 0 ? (
              <button
                type="button"
                onClick={() => navigate("/tutorial/chapter/10")}
                className="flex w-full items-center justify-between rounded-md px-3 py-3 text-left text-sm hover:bg-accent"
                data-testid="tutorial-search-empty"
              >
                <span>
                  Aucun résultat pour «&nbsp;<strong>{q}</strong>&nbsp;».
                  <span className="text-muted-foreground">
                    {" "}
                    Consultez le Chapitre 10 — FAQ.
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ) : (
              results.map((r, i) => {
                const chap = getChapterById(r.item.chapter);
                return (
                  <button
                    key={r.item.id}
                    type="button"
                    role="option"
                    onClick={() => selectResult(r.item)}
                    className="flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left text-sm hover:bg-accent"
                    data-testid={`tutorial-search-result-${i}`}
                  >
                    <span className="mt-0.5 shrink-0 rounded-md bg-brand-blue px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white dark:bg-brand-gold dark:text-background">
                      Chapitre {String(r.item.chapter).padStart(2, "0")}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">
                        {r.item.title}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {excerptWithHighlight(r.item.content, r.matches, "content")}
                      </span>
                      {chap && (
                        <span className="mt-0.5 block text-[10px] text-muted-foreground/70">
                          {chap.title}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Section C — Progress bar
// ────────────────────────────────────────────────────────────────────────

function ProgressPanel({ progress }) {
  const navigate = useNavigate();
  const { exportCert } = useTutorialPDF();
  const total = CHAPTERS.length;
  const done = Object.values(progress).filter(Boolean).length;
  const pct = Math.round((done / total) * 100);
  const nextChapter = CHAPTERS.find((c) => progress[String(c.id)] !== true);
  const allDone = !nextChapter;

  const onCertificate = () => exportCert();

  return (
    <section
      className="rounded-xl border border-border bg-card p-6"
      data-testid="tutorial-progress-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Votre progression
        </div>
        <div
          className="text-sm font-semibold text-foreground"
          data-testid="tutorial-progress-count"
        >
          {done} / {total} chapitres complétés
          <span className="ml-2 text-muted-foreground">({pct}%)</span>
        </div>
      </div>

      <div
        className="mt-3 h-3 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        data-testid="tutorial-progress-bar"
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-brand-blue to-brand-gold transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="mt-4">
        {allDone ? (
          <Button
            onClick={onCertificate}
            data-testid="tutorial-certificate-btn"
            className="bg-brand-gold text-background hover:bg-brand-gold/90"
          >
            <Award className="mr-2 h-4 w-4" />
            Tutoriel complété — Télécharger le certificat
          </Button>
        ) : (
          <Button
            onClick={() => navigate(`/tutorial/chapter/${nextChapter.id}`)}
            data-testid="tutorial-resume-btn"
            className="bg-brand-blue text-white hover:bg-brand-blue/90 dark:bg-brand-gold dark:text-background dark:hover:bg-brand-gold/90"
          >
            <PlayCircle className="mr-2 h-4 w-4" />
            Reprendre — Chapitre {String(nextChapter.id).padStart(2, "0")} :{" "}
            {nextChapter.title}
          </Button>
        )}
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Section D — Chapters grid
// ────────────────────────────────────────────────────────────────────────

function StatusBadge({ status, chapterId }) {
  const map = {
    done: {
      label: "Complété",
      Icon: CheckCircle2,
      className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    },
    current: {
      label: "En cours",
      Icon: CircleDot,
      className: "bg-brand-blue/15 text-brand-blue dark:text-brand-gold",
    },
    todo: {
      label: "À faire",
      Icon: Circle,
      className: "bg-muted text-muted-foreground",
    },
  };
  const cfg = map[status];
  const Icon = cfg.Icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
        cfg.className
      )}
      data-testid={`tutorial-home-status-${chapterId}`}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function ChaptersGrid({ progress }) {
  return (
    <section
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="tutorial-chapters-grid"
    >
      {CHAPTERS.map((ch) => {
        const status = getStatus(progress, ch.id);
        const num = String(ch.id).padStart(2, "0");
        return (
          <Link
            key={ch.id}
            to={`/tutorial/chapter/${ch.id}`}
            data-testid={`tutorial-home-card-${ch.id}`}
            className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-lg"
          >
            <Card
              className={cn(
                "h-full border transition-all hover:-translate-y-0.5 hover:shadow-md",
                status === "current" &&
                  "border-brand-blue/40 ring-1 ring-brand-blue/30 dark:border-brand-gold/40 dark:ring-brand-gold/30"
              )}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <span
                    className="text-2xl leading-none"
                    aria-hidden="true"
                    data-testid={`tutorial-home-emoji-${ch.id}`}
                  >
                    {ch.emoji}
                  </span>
                  <StatusBadge status={status} chapterId={ch.id} />
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Chapitre {num}
                </div>
                <CardTitle className="mt-1 text-base leading-snug">
                  {ch.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-between pt-0 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {ch.durationLabel}
                </span>
                <span className="inline-flex items-center gap-1 text-brand-blue transition-transform group-hover:translate-x-0.5 dark:text-brand-gold">
                  Ouvrir
                  <ChevronRight className="h-3.5 w-3.5" />
                </span>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Section E — Quick start
// ────────────────────────────────────────────────────────────────────────

function QuickStartCard() {
  return (
    <Link
      to="/tutorial/quickstart"
      data-testid="tutorial-quickstart-card"
      className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-xl"
    >
      <div className="flex flex-col gap-4 rounded-xl border-2 border-brand-gold/70 bg-gradient-to-br from-brand-gold/10 via-background to-background p-6 transition-all group-hover:shadow-lg sm:flex-row sm:items-center">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-gold text-background shadow-sm">
          <Zap className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
            Démarrage rapide · 5 min
          </div>
          <div className="mt-0.5 font-judicial text-lg font-bold leading-snug">
            Maîtriser l'essentiel en 5 minutes
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            Les 5 actions clés : créer un projet, importer un PDF, analyser,
            juridique, exporter.
          </div>
        </div>
        <span className="inline-flex items-center gap-1 self-start rounded-full bg-brand-blue px-3 py-1.5 text-xs font-semibold text-white transition-transform group-hover:translate-x-0.5 dark:bg-brand-gold dark:text-background sm:self-center">
          Démarrer
          <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────

export default function TutorialHome() {
  const [progress, setProgress] = useState(() => readProgress());

  useEffect(() => {
    const refresh = () => setProgress(readProgress());
    window.addEventListener("vtpTutorialProgress:change", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("vtpTutorialProgress:change", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return (
    <div className="space-y-10 pb-10" data-testid="tutorial-home">
      <Hero />
      <SearchBar />
      <ProgressPanel progress={progress} />
      <QuickStartCard />
      <div>
        <div className="mb-3 flex items-end justify-between">
          <h2 className="font-judicial text-xl font-black tracking-tight sm:text-2xl">
            Les 11 chapitres
          </h2>
          <span className="text-xs text-muted-foreground">
            Cliquez sur un chapitre pour l'ouvrir
          </span>
        </div>
        <ChaptersGrid progress={progress} />
      </div>

      <section
        className="rounded-xl border border-brand-gold/40 bg-gradient-to-br from-brand-gold/10 via-card to-card p-6 text-center"
        data-testid="tutorial-pdf-export-section"
      >
        <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
          Tutoriel intégral
        </div>
        <h3 className="mt-1 font-judicial text-xl font-black sm:text-2xl">
          Téléchargez le guide complet
        </h3>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
          Un PDF unique, prêt à imprimer et à archiver, avec couverture,
          sommaire, les onze chapitres et l'annexe des formules vitalistes.
        </p>
        <div className="mt-4 flex justify-center">
          <TutorialPDFExportButton variant="primary" />
        </div>
      </section>
    </div>
  );
}
